import { css } from '@emotion/css';
import classnames from 'classnames';
import { Button, ButtonRounded, Font, Loader, Notification } from 'is24-corecss';
import React, { useCallback, useState } from 'react';
import MietrechtsberatungModifyForm from './MietrechtsberatungModifyForm';
import { useHasMeinPlus } from '../../../../../../customHooks/useHasMeinPlus.hook';
import type { ProfileBenefitsStatusResponse } from '../../../../../../@types/productBenefits';
import { ProfileBenefitType } from '../../../../../../@types/productBenefits';
import {
  useGetProductBenefitsPersonalData,
  useProductBenefitsCancelBenefit,
  useProductBenefitsModifyData,
} from '../../../../../../apiHooks/productBenefits.hook';
import { report } from '../../../../../../tracking/tracking';
import { OverviewTrackingEvents } from '../../../../../../tracking/trackingEvents';
import { useGetProductsStatus } from '../../../../../../apiHooks/productsStatus.hook';
import { useProductStatus } from '../../../../../../customHooks/useProductStatus.hook';
import { ProductsStatusKey } from '../../../../../../@types/productsStatus';
import { useIntl } from '@/i18n';

const screenSizeS = '576px';
const styles = {
  container: css`
    border: 1px solid rgb(214, 214, 214);
    border-radius: 4px;
    padding: 24px 16px 0 16px;

    @media screen and (min-width: ${screenSizeS}) {
      width: 530px;
    }
  `,
  statusContainer: css`
    display: flex;
    align-items: center;
    margin-bottom: 24px;
  `,
  positiveIcon: css`
    color: #00d9b1;
    font-size: 20px;
  `,
  neutralIcon: css`
    color: #333333;
  `,
  statusTitle: css`
    margin-left: 12px;
    font-size: 14px;
  `,
  sectionContainer: css`
    padding-bottom: 24px;

    @media screen and (min-width: ${screenSizeS}) {
      display: flex;
      justify-content: space-between;
    }
  `,
  sectionTitle: css`
    font-size: 14px;
    font-weight: bold;
    padding-bottom: 8px;

    @media screen and (min-width: ${screenSizeS}) {
      width: 50%;
      padding-bottom: initial;
    }
  `,
  sectionText: css`
    font-size: 14px;

    @media screen and (min-width: ${screenSizeS}) {
      width: 50%;
    }
  `,
  cancellationContainer: css`
    display: flex;
    flex-direction: column;
    align-items: flex-start;

    & > * {
      margin-top: 12px;
    }
  `,
  cancellationText: css`
    font-size: 16px;
  `,
  loaderContainer: css`
    z-index: 1;
    position: absolute;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.05);
    display: flex;
    justify-content: center;
    align-items: center;
  `,
  lightgray: css`
    color: var(--gray-800);
  `,
};

export const RenderSection = ({ title, content }: { title: string; content: React.JSX.Element }) => (
  <div className={styles.sectionContainer}>
    <p className={styles.sectionTitle}>{title}</p>
    <div className={styles.sectionText}>{content}</div>
  </div>
);

const InsuranceStatus = () => {
  const { t } = useIntl();
  const [isCancellationConfirmation, setIsCancellationConfirmation] = useState(false);
  const [modificationForm, setModificationForm] = useState(false);
  const product = useProductStatus(ProductsStatusKey.ProfileBenefits) as ProfileBenefitsStatusResponse;
  const { areProductsRefetching } = useGetProductsStatus();
  const { personalData } = useGetProductBenefitsPersonalData();
  const isMeinPlus = useHasMeinPlus();
  const { mutate, isLoading } = useProductBenefitsCancelBenefit();
  const { mutate: mutateProductBenefitModify, isLoading: isLoadingProductBenefitModify } =
    useProductBenefitsModifyData();
  const showLoading = areProductsRefetching || isLoadingProductBenefitModify || isLoading;

  const handleCancelBenefit = useCallback(async () => {
    await mutate(ProfileBenefitType.RENT_LEGAL_ADVICE);
    await report(OverviewTrackingEvents.RENT_LEGAL.FUNNEL_CANCELLATION, isMeinPlus);
    // TODO: post-migration handle error catch with alert
  }, [mutate, isMeinPlus]);

  const handleModifyBenefit = useCallback(
    async (values: any) => {
      try {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { policyNumber, ...valuesToSubmit } = values;
        await mutateProductBenefitModify(valuesToSubmit);
        await report(OverviewTrackingEvents.RENT_LEGAL.FUNNEL_MODIFY, isMeinPlus);
      } catch {
        // TODO: post-migration handle error catch with alert
      } finally {
        setModificationForm(false);
      }
    },
    [isMeinPlus, mutateProductBenefitModify],
  );

  return (
    <div className={styles.container}>
      <div className={styles.statusContainer}>
        <i className={classnames('is24-icon-accept', styles.positiveIcon)} />
        <span className={styles.statusTitle}>
          {t({
            id: 'benefits.rentLegal.modal.overview.request.keyEmergencyService.insuranceStatus.title',
            defaultMessage: '',
          })}{' '}
          <Button textStyle={true} onClick={() => setIsCancellationConfirmation(true)}>
            {t({
              id: 'benefits.rentLegal.modal.overview.request.keyEmergencyService.insuranceStatus.deaktivieren',
              defaultMessage: '',
            })}
          </Button>
        </span>
      </div>
      {isCancellationConfirmation && (
        <div className={'margin-bottom-l tooltip-container'}>
          {showLoading && (
            <div className={styles.loaderContainer} data-testid='loader'>
              <Loader />
            </div>
          )}
          <Notification
            title={
              <span className={styles.cancellationText}>
                {t({
                  id: 'benefits.rentLegal.modal.overview.request.keyEmergencyService.insuranceStatus.cancellationText',
                  defaultMessage: '',
                })}
              </span>
            }
            type='info'
            message={
              <div className={styles.cancellationContainer}>
                <Button appearance={'primary'} size={'small'} onClick={handleCancelBenefit} disabled={isLoading}>
                  {t({
                    id: 'benefits.rentLegal.modal.overview.request.keyEmergencyService.insuranceStatus.cancellationButton',
                    defaultMessage: '',
                  })}
                </Button>
                <Button textStyle={true} onClick={() => setIsCancellationConfirmation(false)}>
                  {t({
                    id: 'benefits.rentLegal.modal.overview.request.keyEmergencyService.insuranceStatus.cancellationButtonCancel',
                    defaultMessage: '',
                  })}
                </Button>
              </div>
            }
          />
        </div>
      )}
      <RenderSection
        title={t({
          id: 'benefits.rentLegal.modal.overview.request.keyEmergencyService.insuranceStatus.kundennummer',
          defaultMessage: '',
        })}
        content={<p>{product?.customerNumber}</p>}
      />
      <RenderSection
        title={t({
          id: 'benefits.rentLegal.modal.overview.request.keyEmergencyService.insuranceStatus.versicherungsnummer',
          defaultMessage: '',
        })}
        content={<p>{personalData?.policyNumber}</p>}
      />
      {modificationForm ? (
        <MietrechtsberatungModifyForm
          isLoading={showLoading}
          close={() => setModificationForm(false)}
          onSubmit={handleModifyBenefit}
        />
      ) : (
        <RenderSection
          title={t({
            id: 'benefits.rentLegal.modal.overview.request.keyEmergencyService.insuranceStatus.anschrift',
            defaultMessage: '',
          })}
          content={
            <>
              <p>
                {personalData?.firstName} {personalData?.lastName}
              </p>
              <p>
                {personalData?.street} {personalData?.houseNumber}
              </p>
              <p>
                {personalData?.postcode} {personalData?.city}
              </p>
              <ButtonRounded
                onClick={() => setModificationForm(true)}
                className='margin-top-xs'
                variant='outlineStrong'
                endElement={<i className='is24-icon-edit' />}
              >
                {t({
                  id: 'benefits.rentLegal.modal.overview.request.keyEmergencyService.insuranceStatus.edit',
                  defaultMessage: '',
                })}
              </ButtonRounded>
            </>
          }
        />
      )}

      <RenderSection
        title={t({
          id: 'benefits.rentLegal.modal.overview.request.keyEmergencyService.insuranceStatus.kontaktdaten',
          defaultMessage: '',
        })}
        content={
          <>
            <a
              href={`tel:${t({
                id: 'benefits.rentLegal.modal.overview.request.keyEmergencyService.insuranceStatus.customerService.phoneNumber',
                defaultMessage: '0221 80247 136',
              })}`}
              className='font-s'
            >
              <i className={`is24-icon-telephone`} />
              {t({
                id: 'benefits.rentLegal.modal.overview.request.keyEmergencyService.insuranceStatus.customerService.phoneNumber',
                defaultMessage: '',
              })}
            </a>
            <Font className={`margin-bottom-m ${styles.lightgray}`}>
              {t({
                id: 'benefits.rentLegal.modal.overview.request.keyEmergencyService.insuranceStatus.customerService.phoneNumberText',
                defaultMessage: 'Täglich rund um die Uhr erreichbar',
              })}
            </Font>
            <a
              className='font-s'
              href={`mailto:${t({
                id: 'benefits.rentLegal.modal.overview.request.keyEmergencyService.insuranceStatus.customerService.email',
                defaultMessage: 'immobilienscout24@axa-assistance.de',
              })}`}
            >
              <i className={`is24-icon-message-email`} />
              {t({
                id: 'benefits.rentLegal.modal.overview.request.keyEmergencyService.insuranceStatus.customerService.email',
                defaultMessage: 'immobilienscout24@axa-assistance.de',
              })}
            </a>
          </>
        }
      />
    </div>
  );
};

export default InsuranceStatus;
